/**
 * Permission bits, mirroring src/authorization/enums/permissions.enum.ts on the server.
 * BigInt because bit 31 is negative with JS 32-bit bitwise ops. The check is "all
 * listed bits", like the server's PermissionsGuard. ROUNDS reuses the GAMES bits.
 * UI gating is a convenience only: the server enforces every route.
 */
export const PERMISSIONS = {
  VIEW_LIST_OPERATORS: 1n,
  VIEW_SINGLE_OPERATORS: 2n,
  UPDATE_OPERATORS: 4n,
  DELETE_OPERATORS: 8n,
  VIEW_LIST_GAMES: 16n,
  VIEW_SINGLE_GAMES: 32n,
  UPDATE_GAMES: 64n,
  DELETE_GAMES: 128n,
  VIEW_LIST_PLAYERS: 256n,
  VIEW_SINGLE_PLAYERS: 512n,
  UPDATE_PLAYERS: 1024n,
  DELETE_PLAYERS: 2048n,
  VIEW_LIST_USERS: 4096n,
  VIEW_SINGLE_USERS: 8192n,
  UPDATE_USERS: 16384n,
  DELETE_USERS: 32768n,
  VIEW_LIST_BRANDS: 65536n,
  VIEW_SINGLE_BRANDS: 131072n,
  UPDATE_BRANDS: 262144n,
  DELETE_BRANDS: 524288n,
  VIEW_LIST_CURRENCIES: 1048576n,
  VIEW_SINGLE_CURRENCIES: 2097152n,
  UPDATE_CURRENCIES: 4194304n,
  DELETE_CURRENCIES: 8388608n,
  VIEW_LIST_PROMOTIONS: 16777216n,
  VIEW_SINGLE_PROMOTIONS: 33554432n,
  UPDATE_PROMOTIONS: 67108864n,
  DELETE_PROMOTIONS: 134217728n,
  VIEW_LIST_DEMOAREA: 268435456n,
  VIEW_SINGLE_DEMOAREA: 536870912n,
  UPDATE_DEMOAREA: 1073741824n,
  DELETE_DEMOAREA: 2147483648n,
} as const;

export type Permission = keyof typeof PERMISSIONS;

/** Rounds share the games bits on the server. */
export const ROUNDS = {
  VIEW_LIST_ROUNDS: 'VIEW_LIST_GAMES',
  VIEW_SINGLE_ROUNDS: 'VIEW_SINGLE_GAMES',
  UPDATE_ROUNDS: 'UPDATE_GAMES',
  DELETE_ROUNDS: 'DELETE_GAMES',
} as const satisfies Record<string, Permission>;

export const SUPER_ADMIN_MASK = 0xffffffffn;

/** The JWT/login `Permission` is a BIGINT sent as a string. Anything unparsable grants nothing. */
export function parsePermissionMask(raw: unknown): bigint {
  const s = typeof raw === 'number' ? String(Math.trunc(raw)) : typeof raw === 'string' ? raw.trim() : '';
  if (!/^\d+$/.test(s)) return 0n;
  return BigInt(s) & SUPER_ADMIN_MASK;
}

export function hasPermissions(mask: bigint, perms: readonly Permission[]): boolean {
  return perms.every((p) => (mask & PERMISSIONS[p]) === PERMISSIONS[p]);
}

/** SUPER_ADMIN is also the server's "cross-operator" root. */
export const isSuperAdmin = (mask: bigint) => (mask & SUPER_ADMIN_MASK) === SUPER_ADMIN_MASK;
